"use client"
import React, { useState, useEffect } from "react";
import exifr from "exifr";
import Files from "react-butterfiles";
import Resizer from "react-image-file-resizer";

import useApi from "@/components/api/useApi";
import "@/components/content/NewFileUpload.css";
import Asset from "@/components/shared/Asset";
import UrlInput from "@/components/shared/UrlInput";
import { v1 as uuidv1 } from "uuid";

const YoutubeDomains = [
  "youtube.com",
  "youtu.be",
  "googlevideo.com",
  "video.google.com",
  "youtube.tv",
  "yt.be",
  "www.youtube.com"
];

export default function NewFileUpload({
  name,
  multiple,
  onSelect,
  selectedAssets,
  transactionId,
  upload,
  gallery,
  onGalleryCallback,
  deferred = false,
  types = ["IMAGE", "AUDIO", "VIDEO"]
}) {
  const [showGallery, setShowGallery] = useState(false);
  const [assets, setAssets] = useState(selectedAssets);
  const [deferredFiles, setDeferredFiles] = useState([]);
  const [showLinkVideo, setShowLinkVideo] = useState(false);

  useEffect(() => {
    setAssets(selectedAssets);
  }, [selectedAssets]);

  const imageFileTypes = ["image/png", "image/jpg", "image/jpeg", "image/heic"];
  const audioFileTypes = [
    "audio/wave",
    "audio/wav",
    "audio/x-wav",
    "audio/mp3",
    "audio/mpeg"
  ];
  const videoFileTypes = [
    "video/mp4",
    "video/mov",
    "video/quicktime",
    "video/m4v",
    "video/mpeg"
  ];
  const api = useApi();

  const toggleGallery = () => setShowGallery(!showGallery);

  const refineExifData = (data, id) => {
    let usefulData = ["ImageWidth", "ImageHeight", "CreateDate"];
    let result = {
      id: id
    };
    let lowercased = s => {
      return s.charAt(0).toLowerCase() + s.slice(1);
    };

    usefulData.map(key => {
      if (data && data[key]) {
        result[lowercased(key)] = data[key];
      } else if (data && data["Exif" + key]) {
        result[lowercased(key)] = data["Exif" + key];
      } else {
        result[lowercased(key)] = null;
      }
    });

    if (result.imageWidth > 1600 || result.imageHeight > 1600) {
      let width = result.imageWidth;
      let height = result.imageHeight;
      let maxSide = Math.max(width, height);
      let reduceRatio = maxSide / 1600;
      result.width = Math.round(width / reduceRatio);
      result.height = Math.round(height / reduceRatio);
    }
    return result;
  };

  const getExifData = async file => {
    // console.log("getExifData in", file);
    var temp = await exifr.parse(file.src.file);
    var rotation = await exifr.rotation(file.src.file);
    var location = await exifr.gps(file.src.file);
    // console.log( "getExifData", temp, rotation, location);
    var result = refineExifData(temp, file.id);
    result.rotation = rotation.deg ? rotation.deg : 0;
    result.latitude = location ? location.latitude : null;
    result.longitude = location ? location.longitude : null;

    return result;
  };

  const addFiles = async addedFiles => {
    // console.log("NewFileUpload addFiles", addedFiles, deferred, assets);
    let tempImages = [];
    let exifArray = [];

    const theFiles = addedFiles.map(async file => {
      let exifData = null;
      if (imageFileTypes.includes(file.type)) {
        exifData = await getExifData(file);
        exifArray.push(exifData);
        try {
          file.src.file = await resizeFile(file.src.file);
        } catch (err) {
          // console.log("Resizing error: " + err);
        }
        tempImages.push(file.src.base64);
      }

      return { file: file.src.file, base64: file.src.base64, exif: exifData };
    });

    Promise.all(theFiles).then(files => {
      // console.log("NewFileUpload theFiles", addedFiles, deferred, files );
      if (deferred) {
        // theFiles contains all the information needed to render
        // the assets

        var theAssets = files.map((theFile, index) => {
          return {
            base64: theFile.base64,
            exifData: theFile.exif,
            id: uuidv1(),
            assetType: "IMAGE"
          };
        });
        var newAssets = [...assets, ...theAssets];
        // console.log("deferred assets", newAssets);
        setAssets(newAssets);
        var newFiles = [...deferredFiles, ...files];
        setDeferredFiles(newFiles);
        onSelect(newAssets, newFiles);
      } else {
        let uploadFiles = files.map((theFile, index) => {
          return name
            ? api.uploadFile(name, theFile.file, theFile.exif)
            : api.uploadFileAnonymously(
                theFile.file,
                transactionId,
                theFile.exif
              );
        });
        Promise.all(uploadFiles)
          .then(uploadedFiles => {
            let myUploadedFiles = [...uploadedFiles];
            myUploadedFiles.forEach((uploadedFile, index) => {
              if (uploadedFile.assetType === "IMAGE") {
                uploadedFile["base64"] = tempImages[index];
                uploadedFile["exifData"] = exifArray[index];
              }
            });
            var newAssets = [...assets, ...myUploadedFiles];
            onSelect(newAssets);
            setAssets(newAssets);
          })
          .catch(err => console.error(err));
      }
    });
  };

  const resizeFile = file => {
    return new Promise(resolve => {
      Resizer.imageFileResizer(
        file,
        1600,
        1600,
        "JPEG",
        100,
        0,
        blob => {
          resolve(blob);
        },
        "blob"
      );
    });
  };

  const deleteThumbnail = id => {
    // e.preventDefault();
    // e.stopPropagation();
    let assetsCopy = [...assets];
    let index = assetsCopy.findIndex(asset => asset.id === id);
    if (index > -1) {
      assetsCopy.splice(index, 1);
      setAssets(assetsCopy);
      onSelect(assetsCopy);
    }
  };

  // let deleteButton = id => {
  //   return (
  //     <span
  //       className="material-icons"
  //       style={{ color: "#090B0B" }}
  //       onClick={e => {
  //         deleteThumbnail(e, id);
  //       }}
  //     >
  //       delete
  //     </span>
  //   );
  // };

  const showUploadError = errors => {
    errors.forEach((error, index) => {
      window.flash_messages.addMessage({
        id: `${error.id}`,
        text: uploadErrorTypes[error.type],
        type: "error"
      });
    });
  };

  const maxSize = "200mb";
  const multipleMaxSize = "600mb";
  const multipleMaxCount = 3;

  const uploadErrorTypes = {
    unsupportedFileType: "The type of file you tried to upload is unsupported.",
    maxSizeExceeded: `One or more files are greater than ${maxSize}.`,
    multipleMaxSizeExceeded: `Cannot upload more than ${multipleMaxSize} at one time.`,
    multipleMaxCountExceeded: `Cannot upload more than ${multipleMaxCount} files at a time.`,
    multipleNotAllowed: "Cannot upload multiple files at the same time."
  };

  const acceptableFileTypes = types => {
    var types = [];
    if (types.includes("IMAGE")) {
      types = types.concat(imageFileTypes);
    }
    if (types.includes("AUDIO")) {
      types = types.concat(audioFileTypes);
    }
    if (types.includes("VIDEO")) {
      types = types.concat(videoFileTypes);
    }
    return types;
  };

  const VideoLink = ({ onAdd }) => {
    const [error, setError] = useState(false);

    const isYouTubeOrVimeo = url => {
      const youtubeDomain = "youtube.com";

      const vimeoDomain = "vimeo.com";

      const urlObject = new URL(url);
      const domain = urlObject.hostname.toLowerCase();

      if (YoutubeDomains.includes(domain)) {
        return "YOUTUBE";
      } else if (domain.includes(vimeoDomain)) {
        return "VIMEO";
      } else {
        return null;
      }
    };

    return (
      <>
        <UrlInput
          name="url"
          value={null}
          loadMetadata={false}
          showThumbnail={false}
          onUrlChange={url => {
            setError(false);
            if (url) {
              var supportedVideoType = isYouTubeOrVimeo(url);
              if (supportedVideoType) {
                onAdd({
                  assetType: "VIDEO",
                  assetSource: supportedVideoType,
                  id: url
                });
              } else {
                setError(true);
              }
            }
          }}
        />
        {error && (
          <p className="error">Only YouTube and Vimeo videos are supported.</p>
        )}
      </>
    );
  };

  return (
    <div className="new-file-upload">
      {assets && assets.length > 0 && (
        <Asset assets={assets} onDelete={id => deleteThumbnail(id)} />
      )}

      {upload && !showGallery && (
        <>
          <Files
            multiple={multiple}
            maxSize={maxSize}
            multipleMaxSize={multipleMaxSize}
            multipleMaxCount={multipleMaxCount}
            accept={acceptableFileTypes(types)}
            convertToBase64={true}
            onSuccess={files => {
              addFiles(files);
            }}
            onError={errors => showUploadError(errors)}
          >
            {({ browseFiles, getDropZoneProps }) => (
              <>
                {showLinkVideo ? (
                  <div className="link-to-video">
                    <button
                      className="a cancel"
                      type="button"
                      onClick={() => setShowLinkVideo(false)}
                    >
                      Cancel
                    </button>
                    <div className="paste-link-here">
                      <VideoLink
                        onAdd={newAsset => {
                          var newAssets = [...assets, ...[newAsset]];
                          onSelect(newAssets);
                          setAssets(newAssets);
                          setShowLinkVideo(false);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div {...getDropZoneProps({ className: "myDropZone" })}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={browseFiles}
                    >
                      Upload media
                    </button>
                    {types.includes("VIDEO") && (
                      <button
                        type="button"
                        className="a"
                        onClick={() => setShowLinkVideo(true)}
                      >
                        Link to a video..
                      </button>
                    )}
                    {gallery && name && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={toggleGallery}
                      >
                        Use existing media
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </Files>
        </>
      )}
      {gallery && showGallery && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onGalleryCallback}
        >
          Add new media
        </button>
      )}
    </div>
  );
}
